import { inject } from '@angular/core';
import { CanActivateFn, CanActivateChildFn, Router, UrlTree, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { PermissionCheckService } from 'app/shared/services/core/permission-check.service';
import { AuthService } from 'app/shared/services/core/auth.service';
import { PerfilEnum } from 'app/shared/models/core/security/perfil.enum';

/**
 * Guard de permissão baseado no perfil do usuário.
 * Verifica se o usuário tem permissão para acessar a rota baseado no seu perfilId.
 *
 * Uso nas rotas:
 * ```typescript
 * {
 *   path: 'admin',
 *   canActivate: [PermissionGuard],
 *   data: { allowedProfiles: [PerfilEnum.ADM_SISTEMA] }
 * }
 * ```
 */
export const PermissionGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): boolean | UrlTree => {
  const permissionService = inject(PermissionCheckService);
  const authService = inject(AuthService);
  const router = inject(Router);

  // Verifica se o usuário está autenticado
  if (!authService.isTokenValid()) {
    return router.parseUrl('/login');
  }

  // Obtém os perfis permitidos da configuração da rota
  const allowedProfiles: PerfilEnum[] = route.data['allowedProfiles'] || [];

  // Se não há restrição de perfil, permite acesso
  if (allowedProfiles.length === 0) {
    return true;
  }

  // Verifica se o usuário tem um dos perfis permitidos
  if (permissionService.hasProfile(...allowedProfiles)) {
    return true;
  }

  // Usuário não tem permissão, redireciona para a página inicial
  return router.parseUrl('/inicio');
};

/**
 * Guard para rotas filhas baseado no perfil do usuário.
 */
export const PermissionChildGuard: CanActivateChildFn = (
  childRoute: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): boolean | UrlTree => {
  const permissionService = inject(PermissionCheckService);
  const authService = inject(AuthService);
  const router = inject(Router);

  // Verifica se o usuário está autenticado
  if (!authService.isTokenValid()) {
    return router.parseUrl('/login');
  }

  // Verifica permissão baseada na URL
  if (permissionService.hasRoutePermission(state.url)) {
    return true;
  }

  // Usuário não tem permissão, redireciona para a página inicial
  return router.parseUrl('/inicio');
};
